import cn from "classnames"
import { useEffect, useState } from "react"
import showdown from "showdown"

import { useCalculateFixes } from "@/api/useTriage"
import {
  Button,
  Descriptions,
  DRAWER_ID_SUFFIX,
  Flex,
  message,
  Space,
  Text,
  Tooltip
} from "@/atoms"
import { NO_VALUE_PLACEHOLDER } from "@/const/default-values"
import { CHECKBOX_ISSUE_DATA_ID } from "@/const/table-constants"
import {
  isCodeIssueType,
  isFullyResolvedNotDismissed,
  isHighProfileIssue,
  isIssueDismissed,
  isIssueResolved,
  type Issue
} from "@/domain/issue"
import { getColorBySeverity, isKodemRisk } from "@/domain/vulnerability"
import {
  DEFAULT_LONG_FORMAT,
  getDateFromSeconds,
  getDateInUserLocale
} from "@/helpers/date.helpers"
import { useQueryClient } from "@/hooks/useHttp"
import { useTranslation } from "@/hooks/useTranslation"
import {
  CheckSquareOutlined,
  IssueTypeIcon,
  KodemDark,
  Language
} from "@/icons"
import { CopyLinkButton } from "@/molecules/CopyLinkButton"
import { IconDismissedTag } from "@/molecules/DismissedTag"
import { HighProfileIcon } from "@/molecules/HighProfileIcon"
import { DescriptionVulnerableCodeIssue } from "@/molecules/IssueDetails/DescriptionVulnerableCodeIssue"
import { IssueStatusAlert } from "@/molecules/IssueStatusAlert"
import { Protected } from "@/molecules/Protected"
import { Resources } from "@/molecules/Resources"
import { RiskTooltip } from "@/molecules/RiskTooltip"
import { TitleDrawer } from "@/molecules/TitleDrawer"
import { CreateTicketButton } from "@/organisms/CreateTicketButton"
import { useIssueAndProjectDescription } from "@/organisms/Drawers/applications/triage/useTriage"
import { SkeletonTitle } from "@/organisms/Drawers/components/SkeletonTitle"
import { useDrawer } from "@/organisms/Drawers/hooks/useDrawer"
import { useLink } from "@/organisms/Drawers/hooks/useLink"
import { LabelModal } from "@/organisms/LabelModal"
import { getIssueRequestFilter } from "@/organisms/LabelModal/applications/useLabelsModal"
import { useIssueTicketingSystems } from "@/organisms/Ticketing/hooks/useIssueTicketingSystems"
import { useTicketing } from "@/organisms/Ticketing/hooks/useTicketing"
import { useDismissIssues } from "@/pages/TriagePage/application/useTriage"
import { DismissModal } from "@/pages/TriagePage/components/molecules/DismissModal"
import { IssueDismissInfo } from "@/pages/TriagePage/components/molecules/DismissModal/IssueDismissInfo"
import { useDismissModal } from "@/pages/TriagePage/hooks/useDismissModal"
import { useIsEvpDemo } from "../../hooks/useIsEvpDemo"
import { useTakenActions } from "../TakenActions"
import { getIssueHTML } from "./getTicketContent"

import styles from "./TitleTriageDrawer.module.scss"

import type { Fix } from "@/api/useTriage.types"
import type { TicketingSystem } from "@/domain/ticketing"
import type { EntityRecord } from "@/organisms/Drawers/applications/common"

export const TitleTriageDrawer: React.FC<
  EntityRecord & { drawerId: string; previousDrawerId?: string }
> = (props) => {
  const recordId = props.record?.id
  const [{ response }, projectDescriptionContext, isAllLoaded] =
    useIssueAndProjectDescription(recordId)
  const record = response?.data?.[0]

  const resources = projectDescriptionContext.response?.data?.resources
  const displayResources = !!resources?.length

  const { openTicketModal } = useTicketing()
  const ticketing = useIssueTicketingSystems(resources)
  const linkToDrawer = useLink(props.drawerId)

  const { t } = useTranslation()
  const { t: tDates } = useTranslation("translation", {
    keyPrefix: "triage.drawer.demo.issueDates"
  })
  const [messageApi, messageContextHolder] = message.useMessage()
  const isDemo = useIsEvpDemo()

  const onOkCallback = (attr?: { reason?: string }) => {
    if (!record) return

    const payload = { [record.projectId]: [record] }

    if (isDismissed) return reopenIssues(payload)

    dismissIssues(payload, attr)
  }

  const queryClient = useQueryClient()
  const [{ dismissIssues, reopenIssues }, context] = useDismissIssues(
    {},
    {
      messageApi,
      showLoadingMessage: true,
      onSuccess: () => queryClient.invalidateQueries(getIssueRequestFilter()),
      componentName: "TitleTriageDrawer"
    },
    null
  )
  const { open, openModal, onOk, onCancel } = useDismissModal({
    onOk: onOkCallback
  })

  const contextCalculateFixes = useCalculateFixes()

  const selector = `[${CHECKBOX_ISSUE_DATA_ID}="${record?.id}"]`
  const [isSelected, setIsSelected] = useState<boolean | null>(null)

  const getIssueBySelector = (id?: string) =>
    id
      ? document
          .getElementById(`${id}${DRAWER_ID_SUFFIX}`)
          ?.querySelector(selector)
      : document.querySelector(selector)
  const { previousDrawerId } = props

  useEffect(() => {
    const el = getIssueBySelector(previousDrawerId)

    if (!el) return setIsSelected(null)

    setIsSelected(!!el.closest(".ant-checkbox-wrapper-checked"))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record, selector, previousDrawerId])

  const { onRowClick } = useDrawer("")
  const { addJira } = useTakenActions(recordId, record?.projectId)

  if (isAllLoaded || !record)
    return (
      <Flex vertical justify="center" className={styles.loading}>
        <SkeletonTitle />
      </Flex>
    )

  const {
    language,
    cwe,
    riskId,
    packageName,
    issueType,
    severity,
    filePath,
    aliases = []
  } = record
  const { description } = cwe || {}
  const isDismissed = isIssueDismissed(record)

  const LanguageComponent = (
    <Tooltip title={language || "unknown"}>
      <span>
        <Language
          noTooltip
          secondary
          language={language}
          className={styles.languageIcon}
        />
      </span>
    </Tooltip>
  )

  const setJiraTicket = (
    record: Issue,
    description: string,
    summary: string,
    system: TicketingSystem,
    onCreated?: () => void
  ) => {
    if (!record) return

    const converter = new showdown.Converter()
    openTicketModal({
      payload: {
        id: record.id,
        description: converter.makeHtml(description),
        summary: summary
      },
      // Fires on modal completion; skip when the user cancelled.
      cb: (result) => {
        if (!result.err) onCreated?.()
      },
      system
    })
  }

  const title = description || riskId
  const status = record.status
  const showResolvedAlert = isFullyResolvedNotDismissed(record)
  const kodemRisk = isKodemRisk(riskId)
  const formatDate = (seconds?: number | null) =>
    seconds
      ? getDateInUserLocale(getDateFromSeconds(seconds), DEFAULT_LONG_FORMAT)
      : NO_VALUE_PLACEHOLDER
  const isHighProfile = isHighProfileIssue(record)

  return (
    <>
      {showResolvedAlert || status === "resolved" || status === "processing" ? (
        <IssueStatusAlert type={showResolvedAlert ? "resolved" : status}>
          {(status === "resolved" || showResolvedAlert) && (
            <IssueStatusAlert.Resolved.Meta record={record} />
          )}
          {status === "processing" && <IssueStatusAlert.Processing.Meta />}
        </IssueStatusAlert>
      ) : (
        <IssueStatusAlert.StatusBar
          key={`${record.id}-${record.status}-${record.dismissedAt}`}
          record={record}
        />
      )}
      <TitleDrawer
        style={
          {
            "--icon-background-color-title-drawer":
              getColorBySeverity(severity),
            "--icon-color-color-title-drawer": "var(--neutral-1-color)"
          } as React.CSSProperties
        }
        classes={{
          container: styles.container,
          title: styles.title,
          aside: styles.aside
        }}
        icon={<IssueTypeIcon type={issueType} />}
        prefix={
          displayResources && (
            <div
              className={styles.resourcesWrapper}
              data-testid="project-title"
            >
              <Resources
                placement="drawer"
                resources={resources}
                t={t}
                onRowClick={onRowClick}
              />
            </div>
          )
        }
        title={title}
        subTitle={
          isCodeIssueType(issueType) ? (
            <DescriptionVulnerableCodeIssue
              language={language}
              LanguageComponent={LanguageComponent}
              filePath={filePath || ""}
            />
          ) : (
            <div className={styles.subTitle}>
              <Descriptions.Label>
                <div className={styles.wrapper}>
                  {isHighProfile && (
                    <HighProfileIcon record={record} placement="bottom" t={t} />
                  )}
                  {riskId && (
                    <RiskTooltip record={record} placement="bottom">
                      {kodemRisk && (
                        <Flex gap={4} align="center">
                          <span>{t("general.by")}</span>
                          <KodemDark className={styles.iconKodem} />
                          <Text
                            underline
                            className={cn(
                              styles.cveLink,
                              styles.titleTextUnderline
                            )}
                          >
                            {t("general.researchTeam")}
                          </Text>
                        </Flex>
                      )}
                      {!kodemRisk && (
                        <Text
                          underline={aliases.length > 0}
                          className={styles.cveLink}
                        >
                          {riskId}
                        </Text>
                      )}
                    </RiskTooltip>
                  )}
                  <span>{t("general.onPackage", { str: "" })}</span>
                  {language && LanguageComponent}
                  <span>{packageName}</span>
                </div>
              </Descriptions.Label>
            </div>
          )
        }
        aside={<CopyLinkButton link={linkToDrawer} />}
      />
      <div className={cn(styles.actionsWrapper, isDemo && styles.withDates)}>
        {!ticketing.isHidden && (
          <Protected
            permission={{ resource: "tickets", action: "write" }}
            shouldDisable
          >
            <CreateTicketButton
              canLink
              issueId={record.id}
              id={`${record.id}-create-jira-ticket`}
              sizeBtn="small"
              typeBtn="secondary"
              disabled={
                isIssueDismissed(record, false) || isIssueResolved(record)
              }
              t={t}
              hasIntegration={ticketing.hasIntegration}
              systems={ticketing.systems}
              isLoading={contextCalculateFixes.isPending}
              outlined={true}
              onClick={async (system) => {
                const res = await contextCalculateFixes.mutateAsync({
                  issuesIds: [record.id]
                })
                const fixes = res.data as Fix[]
                const attr = { t, linkToDrawer }
                const { description, summary } = getIssueHTML(fixes, attr)

                setJiraTicket(record, description, summary, system, addJira)
              }}
            />
          </Protected>
        )}

        <Protected
          permission={{ resource: "issues", action: "write" }}
          shouldDisable
        >
          <Button
            icon={<IconDismissedTag />}
            size="small"
            type="secondary"
            className={styles.button}
            disabled={context.isPending}
            onClick={() => (isDismissed ? onOkCallback() : openModal())}
          >
            {isDismissed ? t("general.reopen") : t("general.dismiss")}
          </Button>
        </Protected>
        <Protected
          permission={{ resource: "labels", action: "write" }}
          shouldDisable
        >
          <LabelModal
            btnSize="small"
            btnType="secondary"
            record={record}
            placement="drawer"
            component="triage_drawer"
          />
        </Protected>
        <Button
          disabled={isSelected === null}
          ghost={!!isSelected}
          type={isSelected ? "primary" : "secondary"}
          size="small"
          icon={<CheckSquareOutlined />}
          className={styles.button}
          onClick={() => {
            ;(getIssueBySelector(previousDrawerId) as HTMLInputElement)?.click()

            setIsSelected(!isSelected)
          }}
        >
          {t(`triage.drawer.${isSelected ? "unselect" : "select"}Issue`)}
        </Button>
        {isDemo && (
          <Space
            size={10}
            className={styles.dates}
            separator={<span className={styles.datesSeparator} />}
          >
            <Text className={styles.date}>
              {`${tDates("discovered")}: ${formatDate(record.discoveredAt)}`}
            </Text>
            <Text className={styles.date}>
              {`${tDates("lastSeen")}: ${formatDate(record.lastSeen)}`}
            </Text>
          </Space>
        )}
      </div>
      <DismissModal
        status="dismiss"
        open={open}
        onOk={onOk}
        onCancel={onCancel}
      >
        <IssueDismissInfo count={1} entityType="issue" />
      </DismissModal>
      {messageContextHolder}
    </>
  )
}
